import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  ExternalLink,
  ChevronDown,
  LayoutDashboard,
  Menu,
  Car,
  Tags,
  ShieldCheck,
  Users,
  Code2,
  BarChart3,
  FileDown,
  ListChecks,
  Eye,
  Share2,
  Pencil,
  Trash2,
  Search,
  Plus,
  Boxes,
  Workflow,
} from "lucide-react";

const HERO_IMAGE = "custom1.webp";

const capabilityCards = [
  {
    icon: Boxes,
    title: "Inventory Management",
    description:
      "Add, edit, and manage product or vehicle listings without touching a developer or a spreadsheet.",
  },
  {
    icon: BarChart3,
    title: "Custom Reporting",
    description:
      "Real-time charts and data views built around the numbers your business actually needs to see.",
  },
  {
    icon: Users,
    title: "User and Role Management",
    description:
      "Control who on your team can see and do what — with role-based access built in from day one.",
  },
  {
    icon: ListChecks,
    title: "Status Tracking",
    description:
      "Track the status of every item, job, order, or listing in real time — available, sold, pending, or custom statuses that match your process.",
  },
  {
    icon: FileDown,
    title: "Data Export",
    description:
      "Download reports, inventory lists, or any data as Excel or CSV — on demand, no IT request needed.",
  },
  {
    icon: Workflow,
    title: "Custom Workflows",
    description:
      "If your business has a process that does not fit existing software — we build the tool that fits it exactly.",
  },
];

const audienceCards = [
  {
    title: "Car Dealerships and Automotive Businesses",
    description:
      "Manage your full inventory — listings, pricing, status, registration details — from one dashboard. Update anything yourself without calling a developer.",
  },
  {
    title: "Service Businesses With Field Operations",
    description:
      "Track jobs, staff, client records, and status in real time. Built around how your business actually runs — not how a generic SaaS thinks it should.",
  },
  {
    title: "Any Business Stuck in Spreadsheets",
    description:
      "If your team is running operations on spreadsheets, WhatsApp, and email chains — a custom dashboard replaces all of it with a tool your whole team can use.",
  },
];

const buildSteps = [
  {
    step: "01",
    title: "Discovery",
    description:
      "We map out your current workflow — what your team does manually, what data needs to be tracked, and what a finished dashboard needs to do.",
  },
  {
    step: "02",
    title: "Design",
    description:
      "We design every screen before writing a line of code. You see exactly what you are getting and approve it before build starts.",
  },
  {
    step: "03",
    title: "Build",
    description:
      "We build the full dashboard — database, backend, and frontend. Clean, fast, and built to be used by non-technical team members every day.",
  },
  {
    step: "04",
    title: "Launch and handover",
    description:
      "You get a live URL, full access, and a walkthrough of everything. Your team can use it from day one. No ongoing platform fees.",
  },
];

const proofCards = [
  {
    title: "Big Boys Motor Club — Langley, BC",
    description:
      "An automotive brand was spending hours updating listings and managing inventory manually. We built a full custom admin dashboard — inventory management, status tracking, brand and model management, real-time charts, and user roles. They now run their entire operation without relying on a developer for day to day updates.",
    cta: "bigboysmotorclub.com",
    href: "https://www.bigboysmotorclub.com",
  },
  {
    title: "Canada RDSP + Punjab RDSP",
    description:
      "Insurance agents had no tool to calculate RDSP grants and bonds. We built two custom branded calculators — a four-step tool that produces a full annual report with charts and downloadable data.",
    cta: "canadardsp.com",
    href: "https://www.canadardsp.com",
  },
];

const faqs = [
  {
    question: "Do we need a developer on our team to manage it after launch?",
    answer:
      "No. That is the point. Every dashboard we build is designed so non-technical team members can use it confidently on day one. Adding a listing, updating a status, pulling a report — all of it is point and click.",
  },
  {
    question: "Can it connect to our existing systems?",
    answer:
      "Often yes. If you have an existing website, CRM, or data source — we can assess whether an integration makes sense during the discovery call.",
  },
  {
    question: "Is this different from off-the-shelf software like a SaaS product?",
    answer:
      "Yes. Off-the-shelf software is built for everyone which means it fits no one perfectly. A custom dashboard is built around your exact workflow, your terminology, your data structure, and your team. There are no unused features and no workarounds.",
  },
  {
    question: "What if our needs change after launch?",
    answer:
      "We build dashboards to be maintainable. Adding a new field, a new status, or a new report after launch is straightforward. We discuss post-launch support options as part of every project.",
  },
  {
    question: "How much does a custom dashboard cost?",
    answer:
      "Every project is scoped individually based on complexity. The best first step is a 20-minute call where we understand what you need — then we give you a clear scope and price. No obligation.",
  },
  {
    question: "How long does a build take?",
    answer:
      "Most dashboards are live within four to six weeks from the discovery call. More complex builds with integrations or large data sets take longer — we give a clear timeline after scoping.",
  },
];

const inventoryRows = [
  {
    id: "1",
    reg: "STK-1001",
    brand: "Mercedes-Benz",
    model: "C-300",
    year: "2017",
    price: "$17,999",
    km: "142,000",
    mileage: "8.4",
    condition: "Active",
    tag: "Excellent",
    status: "Available",
    added: "2026-01-08",
  },
  {
    id: "2",
    reg: "STK-1002",
    brand: "Honda",
    model: "Civic",
    year: "2018",
    price: "$17,998",
    km: "121,000",
    mileage: "7.2",
    condition: "Active",
    tag: "Featured",
    status: "Available",
    added: "2026-01-10",
  },
  {
    id: "3",
    reg: "STK-1003",
    brand: "Hyundai",
    model: "Sonata",
    year: "2021",
    price: "$22,500",
    km: "86,000",
    mileage: "7.8",
    condition: "Active",
    tag: "Clean",
    status: "Available",
    added: "2026-01-13",
  },
  {
    id: "4",
    reg: "STK-1004",
    brand: "GMC",
    model: "Sierra 1500",
    year: "2017",
    price: "$26,500",
    km: "155,000",
    mileage: "11.4",
    condition: "Active",
    tag: "Truck",
    status: "Available",
    added: "2026-01-15",
  },
  {
    id: "5",
    reg: "STK-1005",
    brand: "Nissan",
    model: "Sentra",
    year: "2021",
    price: "$16,999",
    km: "92,000",
    mileage: "6.9",
    condition: "Active",
    tag: "Sold",
    status: "Sold",
    added: "2026-01-18",
  },
  {
    id: "6",
    reg: "STK-1006",
    brand: "Hyundai",
    model: "Elantra",
    year: "2024",
    price: "$21,999",
    km: "18,000",
    mileage: "6.5",
    condition: "Active",
    tag: "New Arrival",
    status: "Available",
    added: "2026-01-21",
  },
  {
    id: "7",
    reg: "STK-1007",
    brand: "Chevrolet",
    model: "Malibu",
    year: "2021",
    price: "$17,500",
    km: "101,000",
    mileage: "7.6",
    condition: "Active",
    tag: "Value",
    status: "Available",
    added: "2026-01-25",
  },
  {
    id: "8",
    reg: "STK-1008",
    brand: "Toyota",
    model: "Corolla Hybrid",
    year: "2022",
    price: "$24,999",
    km: "72,000",
    mileage: "4.5",
    condition: "Active",
    tag: "Hybrid",
    status: "Available",
    added: "2026-01-28",
  },
  {
    id: "9",
    reg: "STK-1009",
    brand: "Land Rover",
    model: "Range Rover Sport",
    year: "2016",
    price: "$34,999",
    km: "138,000",
    mileage: "12.1",
    condition: "Active",
    tag: "Luxury",
    status: "Sold",
    added: "2026-02-02",
  },
];

function Sidebar({ activeScreen }) {
  const navItems = [
    { label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
    { label: "Inventory", icon: Car, key: "inventory" },
    { label: "Models", icon: Tags, key: "models" },
  ];

  return (
    <aside className="hidden w-[230px] shrink-0 border-r border-slate-200 bg-white lg:block">
      <div className="flex min-h-full flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-5">
          <div>
            <div className="text-sm font-extrabold text-slate-950">
              Big Boys Motor Club
            </div>
            <div className="mt-1 text-xs font-semibold text-slate-500">
              Admin Panel
            </div>
          </div>
          <Menu className="h-5 w-5 text-slate-500" />
        </div>

        <div className="space-y-5 px-4 py-5">
          <div className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeScreen === item.key;

              return (
                <div
                  key={item.label}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold ${
                    isActive
                      ? "bg-emerald-50 text-emerald-600"
                      : "text-slate-600"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </div>
              );
            })}
          </div>

          <div>
            <div className="mb-2 px-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-slate-400">
              Admin
            </div>
            <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-600">
              <Code2 className="h-4 w-4" />
              Codes
            </div>
          </div>

          <div>
            <div className="mb-2 px-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-slate-400">
              Masters
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-600">
                <ShieldCheck className="h-4 w-4" />
                Brands
              </div>
              <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-600">
                <ListChecks className="h-4 w-4" />
                Features
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2 px-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-slate-400">
              User Management
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-600">
                <Users className="h-4 w-4" />
                Users
              </div>
              <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-600">
                <ShieldCheck className="h-4 w-4" />
                Roles
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-extrabold uppercase tracking-[0.08em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-extrabold text-slate-950">
        {value}
      </div>
      <div className="mt-1 text-sm font-semibold text-emerald-600">
        {note}
      </div>
    </div>
  );
}

function CarsByBrandChart() {
  const data = [
    ["Chevrolet", 1],
    ["GMC", 1],
    ["Honda", 1],
    ["Hyundai", 2],
    ["Land Rover", 1],
    ["Mercedes-Benz", 1],
    ["Nissan", 1],
    ["Toyota", 1],
    ["Volkswagen", 1],
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h4 className="mb-4 text-sm font-extrabold text-slate-900">
        Cars by Brand
      </h4>

      <div className="space-y-3">
        {data.map(([label, value]) => (
          <div
            key={label}
            className="grid grid-cols-[120px_1fr_24px] items-center gap-3"
          >
            <div className="truncate text-xs font-semibold text-slate-500">
              {label}
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${value * 42}%` }}
              />
            </div>
            <div className="text-right text-xs font-bold text-slate-700">
              {value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RegistrationTrendChart() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h4 className="mb-4 text-sm font-extrabold text-slate-900">
        Registration Year Trend
      </h4>

      <svg viewBox="0 0 520 240" className="h-auto w-full">
        <line
          x1="40"
          y1="200"
          x2="492"
          y2="200"
          stroke="#e2e8f0"
          strokeWidth="2"
        />
        <line
          x1="40"
          y1="34"
          x2="40"
          y2="200"
          stroke="#e2e8f0"
          strokeWidth="2"
        />

        <path
          d="M45 175 C95 170 100 116 145 118 C190 120 190 178 235 170 C278 162 280 96 325 86 C370 76 382 138 425 120 C462 104 470 58 490 54"
          fill="none"
          stroke="#22C55E"
          strokeWidth="5"
          strokeLinecap="round"
        />

        <path
          d="M45 175 C95 170 100 116 145 118 C190 120 190 178 235 170 C278 162 280 96 325 86 C370 76 382 138 425 120 C462 104 470 58 490 54 L490 200 L45 200 Z"
          fill="rgba(34,197,94,0.12)"
        />

        {[
          ["2016", 38],
          ["2017", 88],
          ["2018", 138],
          ["2019", 188],
          ["2020", 238],
          ["2021", 288],
          ["2022", 338],
          ["2023", 388],
          ["2024", 438],
        ].map(([label, x]) => (
          <text key={label} x={x} y="224" fontSize="12" fill="#64748b">
            {label}
          </text>
        ))}
      </svg>
    </div>
  );
}

function DonutChart({ title, items }) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  let cumulative = 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h4 className="mb-4 text-sm font-extrabold text-slate-900">{title}</h4>

      <div className="flex flex-col items-center gap-4 sm:flex-row">
        <svg viewBox="0 0 120 120" className="h-32 w-32 -rotate-90">
          <circle
            cx="60"
            cy="60"
            r="42"
            fill="none"
            stroke="#e2e8f0"
            strokeWidth="18"
          />

          {items.map((item) => {
            const dash = (item.value / total) * 263.89;
            const gap = 263.89 - dash;
            const offset = -cumulative;
            cumulative += dash;

            return (
              <circle
                key={item.label}
                cx="60"
                cy="60"
                r="42"
                fill="none"
                stroke={item.color}
                strokeWidth="18"
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={offset}
              />
            );
          })}
        </svg>

        <div className="w-full space-y-2">
          {items.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2"
            >
              <span className="flex items-center gap-2 text-xs font-bold text-slate-600">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                {item.label}
              </span>
              <span className="text-xs font-extrabold text-slate-800">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DashboardView() {
  return (
    <div>
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h3 className="text-2xl font-extrabold text-slate-950">
            Dashboard
          </h3>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Welcome to Big Boys Motor Club admin panel
          </p>
        </div>

        <div className="flex items-center gap-3 self-start rounded-full bg-white px-4 py-2 shadow-sm">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-xs font-extrabold text-emerald-700">
            B
          </div>
          <span className="text-sm font-bold text-slate-700">bbmaduser</span>
        </div>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="In Stock" value="10" note="Active inventory" />
        <StatCard label="Sold" value="3" note="Total sold units" />
        <StatCard
          label="Inventory Value"
          value="$213,993.89"
          note="Total valuation"
        />
        <StatCard label="Brands" value="49" note="16 models" />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <CarsByBrandChart />
        <RegistrationTrendChart />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <DonutChart
          title="Fuel Type Distribution"
          items={[
            { label: "Gasoline", value: 8, color: "#22C55E" },
            { label: "Diesel", value: 3, color: "#0F172A" },
            { label: "Hybrid", value: 2, color: "#38BDF8" },
          ]}
        />

        <DonutChart
          title="Status Distribution"
          items={[
            { label: "Available", value: 10, color: "#22C55E" },
            { label: "Sold", value: 3, color: "#EF4444" },
          ]}
        />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const isSold = status === "Sold";

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-extrabold ${
        isSold
          ? "bg-red-100 text-red-600"
          : "bg-emerald-100 text-emerald-700"
      }`}
    >
      {status}
    </span>
  );
}

function InventoryView() {
  return (
    <div>
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h3 className="text-2xl font-extrabold text-slate-950">
            Inventory
          </h3>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Manage your vehicle inventory
          </p>
        </div>

        <Button
          type="button"
          className="w-full bg-emerald-500 text-white hover:bg-emerald-600 sm:w-auto"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Vehicle
        </Button>
      </div>

      <div className="mb-5 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_160px_160px]">
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <Search className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-500">
            Search model / reg number...
          </span>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-600">
          All Status
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-600">
          Active
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px] text-left text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.08em] text-slate-500">
              <tr>
                <th className="px-4 py-3">Item ID</th>
                <th className="px-4 py-3">Reg Number</th>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Model</th>
                <th className="px-4 py-3">Year</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">KM Driven</th>
                <th className="px-4 py-3">Mileage</th>
                <th className="px-4 py-3">Condition</th>
                <th className="px-4 py-3">Tag</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {inventoryRows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-700">
                    {row.id}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.reg}</td>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    {row.brand}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.model}</td>
                  <td className="px-4 py-3 text-slate-600">{row.year}</td>
                  <td className="px-4 py-3 font-bold text-slate-900">
                    {row.price}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.km}</td>
                  <td className="px-4 py-3 text-slate-600">{row.mileage}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {row.condition}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.tag}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.added}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-slate-500" />
                      <Share2 className="h-4 w-4 text-slate-500" />
                      <Pencil className="h-4 w-4 text-emerald-500" />
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 text-sm font-semibold text-slate-500">
          Showing 1-13 of 13
        </div>
      </div>
    </div>
  );
}

function DashboardDemo({ activeDemo, setActiveDemo }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-soft sm:p-6"
    >
      <div className="mb-5 grid gap-3 sm:grid-cols-2">
        {[
          { label: "Dashboard View", key: "dashboard" },
          { label: "Inventory View", key: "inventory" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveDemo(tab.key)}
            className={`min-h-[50px] rounded-full px-5 py-3 text-sm font-extrabold transition-all duration-300 ${
              activeDemo === tab.key
                ? "bg-emerald-500 text-white shadow-md"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-100">
        <div className="flex min-h-[720px]">
          <Sidebar activeScreen={activeDemo} />

          <main className="min-w-0 flex-1 p-4 sm:p-6">
            <motion.div
              key={activeDemo}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              {activeDemo === "dashboard" ? (
                <DashboardView />
              ) : (
                <InventoryView />
              )}
            </motion.div>
          </main>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-center">
        <p className="mb-4 text-base font-semibold text-slate-900">
          Want a dashboard like this for your business?
        </p>

        <Button
          asChild
          className="bg-emerald-500 text-white hover:bg-emerald-600"
        >
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2"
          >
            Book a Free Consultation
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </motion.div>
  );
}

export default function CustomDashboardDevelopmentVancouverPage() {
  const [activeDemo, setActiveDemo] = useState("dashboard");
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
  title="Custom Dashboard Development Vancouver | Square Trigger"
  description="We build custom admin dashboards and web applications for businesses that are done managing operations manually. See what we built for a Langley automotive brand."
  ogTitle="Custom Dashboard Development Vancouver | Square Trigger"
  ogDescription="We build custom admin dashboards and web applications for businesses that are done managing operations manually. See what we built for a Langley automotive brand."
  ogSiteName="Square Trigger Custom Dashboard"
  ogImage="https://squaretrigger.com/custom1.webp"
  keywords="custom dashboard development Vancouver, custom admin dashboard, custom tool creation Vancouver, admin dashboard development Vancouver"
/>

{/* Hero Section */}
<section className="relative overflow-hidden">
  {/* Background Image */}
  <div className="absolute inset-0">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
  </div>

  <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
    <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
      {/* Left Content */}
      <div className="text-center lg:text-left">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
        >
          <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
            <LayoutDashboard className="h-4 w-4" />
            <span className="text-sm font-medium">
              Custom Dashboard Development — Vancouver, BC
            </span>
          </Badge>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className='mx-auto m-0 mb-[18px] max-w-[820px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3.25rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
        >
          Still Calling Your Developer Every Time Something Needs{" "}
          <span className="gradient-text">Updating?</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mx-auto m-0 mb-6 max-w-[700px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] lg:mx-0"
        >
          We build custom admin dashboards and web applications that give
          your team full control — no developer needed for day to day
          operations. Here is what we built for a Langley automotive
          brand. And how we can build something similar for yours.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
        >
          <Button
            variant="gradient"
            asChild
            className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
          >
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2"
            >
              Book a Free Consultation
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>

          <Button
            variant="glass"
            asChild
            className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
          >
            <Link
              to="/work"
              className="flex items-center justify-center gap-2"
            >
              See Our Work
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>

      
      <div className="hidden lg:flex items-center justify-center min-h-[420px]">
        <div className="h-[420px] w-full max-w-[520px]" />
      </div>
    </div>
  </div>
</section>
      {/* Problem Section */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-[1000px] text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              The Problem We Solve
            </span>

            <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
              Manual Operations Kill Productivity.{" "}
              <span className="gradient-text">
                A Custom Dashboard Fixes That.
              </span>
            </h2>

            <div className="rounded-2xl border border-border bg-card/60 p-6 text-left shadow-soft backdrop-blur sm:p-8">
              <p className="mb-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Most growing businesses hit the same wall. Inventory gets
                updated by emailing a developer. Listings get posted manually
                one by one. Reports get pulled by logging into three different
                systems. Staff waste hours every week on tasks that a well-built
                dashboard could handle in minutes.
              </p>

              <p className="mb-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Off-the-shelf software is either too generic to fit your
                workflow or too expensive for what you actually need. And a
                developer on call for every small update is not a sustainable
                solution.
              </p>

              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                We build custom admin dashboards and web applications built
                around exactly how your business operates — so your team can
                manage everything themselves without touching a line of code.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Demo Section */}
      {/* <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              What We Built
            </span>

            <h2 className="mx-auto mt-3 max-w-[1000px] text-3xl font-display font-bold text-foreground md:text-4xl">
              A Custom Admin Dashboard for{" "}
              <span className="gradient-text">
                Big Boys Motor Club — Langley, BC.
              </span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              This is the actual dashboard we built. A Langley-based automotive
              brand was managing their entire inventory manually. We built them
              a complete admin system — here is what it looks like.
            </p>
          </motion.div>

          <DashboardDemo activeDemo={activeDemo} setActiveDemo={setActiveDemo} />
        </div>
      </section> */}

      {/* What We Build */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-14"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              What We Can Build For You
            </span>

            <h2 className="mx-auto mt-3 max-w-[1000px] text-3xl font-display font-bold text-foreground md:text-4xl">
              Every Dashboard Is Built Around Your Workflow —{" "}
              <span className="gradient-text">Not a Template.</span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              The BBMC dashboard is one example. The problems we solve with
              custom dashboards and web applications vary by business — but the
              pattern is always the same. Manual work that should not be manual.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {capabilityCards.map((card, index) => {
              const Icon = card.icon;

              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-all duration-300 hover:border-primary/30 sm:items-start sm:text-left">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary sm:mx-0">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                      {card.title}
                    </h3>

                    <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {card.description}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Who We Build For
            </span>

            <h2 className="mx-auto mt-3 max-w-[1000px] text-3xl font-display font-bold text-foreground md:text-4xl">
              If Your Team Is Managing Operations Manually —{" "}
              <span className="gradient-text">This Is For You.</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-3">
            {audienceCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex h-full flex-col items-center rounded-2xl border border-border bg-card p-6 text-center shadow-soft transition-shadow hover:shadow-medium sm:items-start sm:text-left"
              >
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary sm:mx-0">
                  <Users className="h-6 w-6 text-primary-foreground" />
                </div>

                <h3 className="mb-3 text-lg font-display font-semibold text-foreground sm:text-xl">
                  {card.title}
                </h3>

                <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {card.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-12 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              How We Build It
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              From Problem to Live Dashboard{" "}
              <span className="gradient-text">in Four Weeks.</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {buildSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:text-left">
                  <div className="mb-4 text-center text-3xl font-display font-bold gradient-text">
                    {step.step}
                  </div>

                  <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                    {step.title}
                  </h3>

                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {step.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              Our Work
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              Real Businesses.{" "}
              <span className="gradient-text">Real Problems Solved.</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-2">
            {proofCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="flex h-full flex-col rounded-2xl border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:p-8 sm:text-left"
              >
                <h3 className="mb-3 text-xl font-display font-bold sm:text-2xl">
                  {card.title}
                </h3>

                <p className="mb-6 flex-1 leading-relaxed text-background/70">
                  {card.description}
                </p>

                <a
                  href={card.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 font-bold text-secondary hover:underline sm:justify-start"
                >
                  {card.cta}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center sm:mb-12"
            >
              <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
                What Businesses{" "}
                <span className="gradient-text">Usually Ask First</span>
              </h2>
            </motion.div>

            <div className="mx-auto max-w-[1000px] space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
                >
                  <button
                    type="button"
                    onPointerUp={() =>
                      setOpenFaq(openFaq === index ? null : index)
                    }
                    className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
                  >
                    <span className="pr-6 font-semibold text-foreground">
                      {faq.question}
                    </span>

                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
          >
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
              <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
                Your Team Should Not Be Doing This Manually.
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Tell us what your team is managing on spreadsheets, WhatsApp, or
                email. We will tell you exactly what a custom dashboard could
                replace — and what it would cost to build it.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="premium"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background text-foreground hover:bg-background/90 sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  size="lg"
                  asChild
                  className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/15 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link
                    to="/work"
                    className="flex items-center justify-center gap-2"
                  >
                    See Our Work
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}